import type { ParsedUrlQuery } from 'querystring';

import { getPageFromParam } from 'src/utils/queryParams';

export interface ProductsQueryActiveStateFromPageURL {
  activeFacetValuesByKey: Record<string, string[]>;
  activeSort: { descending: boolean; key: string } | null;
  page: number;
  searchTerm?: string;
}

interface AdapterOptions {
  filterPathValues?: Record<string, string[]>;
  includeSearchTerm?: boolean;
}

const ATTRIBUTES_SHORT_PREFIX = 'a.';
const ATTRIBUTES_FULL_PREFIX = 'attributes.';

const getFirstValue = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value;

const expandFacetKey = (shortKey: string): string =>
  shortKey.startsWith(ATTRIBUTES_SHORT_PREFIX)
    ? `${ATTRIBUTES_FULL_PREFIX}${shortKey.slice(ATTRIBUTES_SHORT_PREFIX.length)}`
    : shortKey;

const parseSortValue = (
  rawSort: string | undefined,
): { descending: boolean; key: string } | null => {
  if (!rawSort) {
    return null;
  }

  const lastDashIndex = rawSort.lastIndexOf('-');

  if (lastDashIndex === -1) {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  const direction = rawSort.slice(lastDashIndex + 1);

  if (direction !== 'asc' && direction !== 'desc') {
    return { descending: false, key: expandFacetKey(rawSort) };
  }

  return {
    descending: direction === 'desc',
    key: expandFacetKey(rawSort.slice(0, lastDashIndex)),
  };
};

export const adaptPageURLToProductsQueryActiveState = (
  query: ParsedUrlQuery,
  options: AdapterOptions = {},
): ProductsQueryActiveStateFromPageURL => {
  return {
    activeFacetValuesByKey: options.filterPathValues ?? {},
    activeSort: parseSortValue(getFirstValue(query.sort)),
    page: getPageFromParam(query),
    searchTerm: options.includeSearchTerm ? getFirstValue(query.term) : undefined,
  };
};
